/**
 * URLStateManager
 *
 * Manages search filter state via URL query parameters.
 * Implements Observer pattern to notify subscribers of state changes.
 *
 * SOLID Compliance:
 * - SRP: Only manages URL state
 * - OCP: Extensible via subscription (no modification needed)
 * - LSP: N/A (no inheritance)
 * - ISP: Clean interface with only necessary methods
 * - DIP: Depends on abstractions (React Router's useSearchParams)
 *
 * Usage:
 * const urlStateManager = new URLStateManager(searchParams, setSearchParams);
 * urlStateManager.updateFilter('location', 'Denver');
 * urlStateManager.subscribe((filters) => console.log('Filters changed:', filters));
 *
 * Location: apps/web/src/services/search/URLStateManager.js
 * Created: 2025-10-31
 * Part of: MVP 1.9 - Adaptive Search & Filter System
 */

import { buildSearchUrl, parseSearchParams } from '../searchService';

class URLStateManager {
  /**
   * Create URLStateManager instance
   * @param {URLSearchParams} searchParams - React Router's search params
   * @param {Function} setSearchParams - React Router's setter function
   */
  constructor(searchParams, setSearchParams) {
    this.searchParams = searchParams;
    this.setSearchParams = setSearchParams;
    this.subscribers = new Set(); // Observer pattern subscribers
  }

  /**
   * Get current filter state from URL
   * @returns {Object} - Filter object (e.g., { location: 'Denver', startDate: '2025-11-15' })
   */
  getFilters() {
    return parseSearchParams(this.searchParams);
  }

  /**
   * Update single filter and navigate
   * @param {string} key - Filter key (e.g., 'location')
   * @param {any} value - Filter value
   */
  updateFilter(key, value) {
    const currentFilters = this.getFilters();
    const newFilters = { ...currentFilters, [key]: value };
    this._navigateToFilters(newFilters);
  }

  /**
   * Update multiple filters at once
   * @param {Object} filters - Object with multiple key-value pairs
   */
  updateFilters(filters) {
    const currentFilters = this.getFilters();
    const newFilters = { ...currentFilters, ...filters };
    this._navigateToFilters(newFilters);
  }

  /**
   * Clear single filter and navigate
   * @param {string} key - Filter key to remove
   */
  clearFilter(key) {
    const currentFilters = this.getFilters();
    delete currentFilters[key];

    // Special handling for location (clear all location-related parameters)
    if (key === 'location') {
      delete currentFilters.location;
      delete currentFilters.lat;
      delete currentFilters.lng;
      delete currentFilters.latitude;
      delete currentFilters.longitude;
      delete currentFilters.city;
      delete currentFilters.state;
      delete currentFilters.stateCode;
      delete currentFilters.countryCode;
      delete currentFilters.accuracy;
      delete currentFilters.formattedAddress;
    }

    // Special handling for category taxonomy filters
    // MVP 1.3 Phase 2 - Progressive search: clear only specific level and children
    if (key === 'categoryL1') {
      // Clearing L1 clears everything (L1, L2, L3, and display names)
      delete currentFilters.categoryL1;
      delete currentFilters.categoryL2;
      delete currentFilters.categoryL3;
      delete currentFilters.activityName;
    } else if (key === 'categoryL2') {
      // Clearing L2 only clears L2 and L3 (not L1)
      delete currentFilters.categoryL2;
      delete currentFilters.categoryL3;
    } else if (key === 'categoryL3') {
      // Clearing L3 only clears L3 (not L1 or L2)
      delete currentFilters.categoryL3;
    }

    // Special handling for tag filters (clear associated display names)
    if (key === 'tags') {
      delete currentFilters.tags;
      delete currentFilters.tagNames; // Display names for tags
    }

    // Special handling for legacy activity filter (clear associated display names)
    if (key === 'activitySlug') {
      delete currentFilters.activitySlug;
      delete currentFilters.activityName;
    }

    // Special handling for date range (clear both startDate and endDate together)
    if (key === 'startDate' || key === 'endDate') {
      delete currentFilters.startDate;
      delete currentFilters.endDate;
    }

    this._navigateToFilters(currentFilters);
  }

  /**
   * Clear all filters and navigate to root
   */
  clearAllFilters() {
    this.setSearchParams({});
    this._notifySubscribers();
  }

  /**
   * Get list of active filter keys
   * @returns {Array<string>} - Array of filter keys with values
   */
  getActiveFilters() {
    const filters = this.getFilters();
    return Object.keys(filters).filter(key => {
      const value = filters[key];
      return value !== undefined && value !== null && value !== '';
    });
  }

  /**
   * Get active filters with display labels for pills
   * @returns {Array<Object>} - Array of { key, value, displayLabel, icon }
   */
  getActiveFiltersForDisplay() {
    const filters = this.getFilters();
    const activeFilters = [];

    // Location filter
    if (filters.location) {
      activeFilters.push({
        key: 'location',
        value: filters.location,
        displayLabel: `📍 ${filters.location}`,
        icon: '📍'
      });
    }

    // Date range filter
    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      const formattedStart = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const formattedEnd = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      activeFilters.push({
        key: 'dates',
        value: { startDate: filters.startDate, endDate: filters.endDate },
        displayLabel: `📅 ${formattedStart} - ${formattedEnd}`,
        icon: '📅'
      });
    } else if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      const formatted = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      activeFilters.push({
        key: 'dates',
        value: { startDate: filters.startDate },
        displayLabel: `📅 ${formatted}`,
        icon: '📅'
      });
    }

    // Guest count filter
    if (filters.partySize && filters.partySize > 1) {
      activeFilters.push({
        key: 'partySize',
        value: filters.partySize,
        displayLabel: `👥 ${filters.partySize} guests`,
        icon: '👥'
      });
    }

    // Category taxonomy filter (MVP 1.3 Phase 2) - supports comma-separated categories
    if (filters.categoryL1) {
      const slugs = filters.categoryL1.split(',').map(s => s.trim());
      const names = filters.activityName ? filters.activityName.split(',').map(n => n.trim()) : slugs;

      if (slugs.length === 1) {
        // Single category
        activeFilters.push({
          key: 'categoryL1',
          value: filters.categoryL1,
          displayLabel: `🏔️ ${names[0]}`,
          icon: '🏔️'
        });
      } else {
        // Multiple categories - show as combined pill
        activeFilters.push({
          key: 'categoryL1',
          value: filters.categoryL1,
          displayLabel: `🏔️ ${slugs.length} activities`,
          icon: '🏔️'
        });
      }
    }

    // Activity type filter (legacy - supports comma-separated multiple activities)
    else if (filters.activitySlug) {
      const slugs = filters.activitySlug.split(',').map(s => s.trim());
      const names = filters.activityName ? filters.activityName.split(',').map(n => n.trim()) : slugs;

      if (slugs.length === 1) {
        // Single activity
        activeFilters.push({
          key: 'activitySlug',
          value: filters.activitySlug,
          displayLabel: `🏔️ ${names[0]}`,
          icon: '🏔️'
        });
      } else {
        // Multiple activities - show as combined pill
        activeFilters.push({
          key: 'activitySlug',
          value: filters.activitySlug,
          displayLabel: `🏔️ ${slugs.length} activities`,
          icon: '🏔️'
        });
      }
    }

    // Price range filter
    if (filters.minPrice !== undefined || filters.maxPrice !== undefined) {
      const minPrice = filters.minPrice || 0;
      const maxPrice = filters.maxPrice || '∞';
      activeFilters.push({
        key: 'priceRange',
        value: { minPrice: filters.minPrice, maxPrice: filters.maxPrice },
        displayLabel: `💰 $${minPrice} - ${maxPrice === '∞' ? maxPrice : '$' + maxPrice}`,
        icon: '💰'
      });
    }

    // Rating filter
    if (filters.minRating) {
      const stars = '⭐'.repeat(filters.minRating);
      activeFilters.push({
        key: 'minRating',
        value: filters.minRating,
        displayLabel: `${stars}+`,
        icon: '⭐'
      });
    }

    // Tag filters (MVP 1.3 Phase 2)
    if (filters.tags) {
      const tagSlugs = filters.tags.split(',').map(s => s.trim());
      const tagNames = filters.tagNames ? filters.tagNames.split(',').map(n => n.trim()) : tagSlugs;

      if (tagSlugs.length === 1) {
        // Single tag
        activeFilters.push({
          key: 'tags',
          value: filters.tags,
          displayLabel: `🏷️ ${tagNames[0]}`,
          icon: '🏷️'
        });
      } else {
        // Multiple tags - show count
        activeFilters.push({
          key: 'tags',
          value: filters.tags,
          displayLabel: `🏷️ ${tagSlugs.length} tags`,
          icon: '🏷️'
        });
      }
    }

    return activeFilters;
  }

  /**
   * Check if any filters are active
   * @returns {boolean}
   */
  hasFilters() {
    return this.getActiveFilters().length > 0;
  }

  /**
   * Subscribe to filter state changes (Observer pattern)
   * @param {Function} callback - Called when filters change: callback(filters)
   * @returns {Function} - Unsubscribe function
   */
  subscribe(callback) {
    if (typeof callback !== 'function') {
      throw new Error('URLStateManager.subscribe() requires a callback function');
    }

    this.subscribers.add(callback);

    // Return unsubscribe function
    return () => this.unsubscribe(callback);
  }

  /**
   * Unsubscribe from filter state changes
   * @param {Function} callback - Callback to remove
   */
  unsubscribe(callback) {
    this.subscribers.delete(callback);
  }

  /**
   * Get number of active subscribers (for debugging)
   * @returns {number}
   */
  getSubscriberCount() {
    return this.subscribers.size;
  }

  /**
   * Build URL from filters (delegates to searchService)
   * @param {Object} filters - Filter object
   * @returns {string} - URL with query parameters
   */
  buildUrl(filters) {
    return buildSearchUrl(filters);
  }

  /**
   * Parse URL to filters (delegates to searchService)
   * @param {URLSearchParams} searchParams - URL search parameters
   * @returns {Object} - Filter object
   */
  parseUrl(searchParams) {
    return parseSearchParams(searchParams);
  }

  /**
   * Internal: Navigate to new filter state
   * @private
   */
  _navigateToFilters(filters) {
    const params = new URLSearchParams();

    // Build URL parameters from filters (using same logic as buildQueryString in searchService)
    // Location filters
    if (filters.location) {params.append('location', filters.location);}
    if (filters.lat !== undefined) {params.append('lat', filters.lat);}
    if (filters.lng !== undefined) {params.append('lng', filters.lng);}
    if (filters.latitude !== undefined) {params.append('latitude', filters.latitude);}
    if (filters.longitude !== undefined) {params.append('longitude', filters.longitude);}
    if (filters.radius) {params.append('radius', filters.radius);}
    if (filters.radiusUnit) {params.append('radiusUnit', filters.radiusUnit);}

    // Address components from autocomplete (for coordinate pass-through)
    if (filters.city !== undefined) {params.append('city', filters.city);}
    if (filters.state !== undefined) {params.append('state', filters.state);}
    if (filters.stateCode !== undefined) {params.append('stateCode', filters.stateCode);}
    if (filters.countryCode !== undefined) {params.append('countryCode', filters.countryCode);}
    if (filters.accuracy) {params.append('accuracy', filters.accuracy);}
    if (filters.formattedAddress) {params.append('formattedAddress', filters.formattedAddress);}

    // Category taxonomy filters (MVP 1.3 Phase 2)
    if (filters.categoryL1) {params.append('categoryL1', filters.categoryL1);}
    if (filters.categoryL2) {params.append('categoryL2', filters.categoryL2);}
    if (filters.categoryL3) {params.append('categoryL3', filters.categoryL3);}

    // Tag filters (MVP 1.3 Phase 2)
    if (filters.tags) {params.append('tags', filters.tags);}

    // Activity filters (legacy - for backward compatibility)
    if (filters.activityId) {params.append('activityId', filters.activityId);}
    if (filters.activitySlug) {params.append('activitySlug', filters.activitySlug);}
    if (filters.activityName) {params.append('activityName', filters.activityName);}

    // Date filters
    if (filters.startDate) {params.append('startDate', filters.startDate);}
    if (filters.endDate) {params.append('endDate', filters.endDate);}
    if (filters.requireAllDates !== undefined) {params.append('requireAllDates', filters.requireAllDates);}

    // Price filters
    if (filters.minPrice !== undefined) {params.append('minPrice', filters.minPrice);}
    if (filters.maxPrice !== undefined) {params.append('maxPrice', filters.maxPrice);}
    if (filters.partySize) {params.append('partySize', filters.partySize);}

    // Rating filter
    if (filters.minRating !== undefined) {params.append('minRating', filters.minRating);}

    // Sorting and pagination
    if (filters.sortBy) {params.append('sortBy', filters.sortBy);}
    if (filters.limit) {params.append('limit', filters.limit);}
    if (filters.offset !== undefined) {params.append('offset', filters.offset);}

    // Country hint
    if (filters.country) {params.append('country', filters.country);}

    // Update URL (React Router will handle navigation)
    this.setSearchParams(params);

    // Notify subscribers of state change
    this._notifySubscribers();
  }

  /**
   * Internal: Notify all subscribers of state change
   * @private
   */
  _notifySubscribers() {
    const filters = this.getFilters();

    // Call each subscriber with current filter state
    this.subscribers.forEach(callback => {
      try {
        callback(filters);
      } catch (error) {
        console.error('[URLStateManager] Subscriber callback error:', error);
      }
    });
  }
}

export default URLStateManager;
